import { useEffect, type ReactNode } from 'react'
import { CloseIcon } from '@/components/game/GameIcons'

/**
 * Plein écran via un overlay fixe plein viewport (fiable cross-navigateur, mobile
 * inclus — pas d'API Fullscreen native). Inactif : rend les enfants dans le flux.
 * Actif : overlay `position: fixed`, z élevé, bouton de sortie (coin haut-gauche),
 * Échap pour fermer, scroll de page verrouillé puis restauré.
 */
export function FullscreenContainer({
  active,
  onExit,
  children,
}: {
  active: boolean
  onExit: () => void
  children: ReactNode
}) {
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [active, onExit])

  if (!active) return <>{children}</>

  return (
    <div role="dialog" aria-modal="true" aria-label="Carte en plein écran" className="fixed inset-0 z-50 bg-ocean-900 flex flex-col">
      <button
        type="button"
        onClick={onExit}
        aria-label="Quitter le plein écran"
        title="Quitter le plein écran"
        className="absolute top-3 left-3 z-30 grid place-items-center w-9 h-9 rounded-lg bg-ocean-900/85 border border-sand-500/25 text-sand-200 hover:text-sand-100 backdrop-blur-sm shadow-lg"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
      {/* Center the (square) map so it never gets stretched to the screen's aspect
          ratio — a wide container + the SVG layers' preserveAspectRatio="none" would
          elongate the fleet hulls. The map keeps its 1:1 board, letterboxed on the sides. */}
      <div className="relative flex-1 min-h-0 grid place-items-center">{children}</div>
    </div>
  )
}
